import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, ShieldAlert, Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Set by the sign-in page when THIS browser asked for a link, so the same-device flow stays
// one tap. Any other opener (a phone, a mail app's built-in browser, a link scanner) has to
// press Continue — which is what stops scanners from burning the link before the person.
export const MAGIC_PENDING_KEY = "rl_ml_pending";
const PENDING_TTL_MS = 30 * 60 * 1000;

const sameDeviceRequested = () => {
  try {
    const t = parseInt(localStorage.getItem(MAGIC_PENDING_KEY) || "0", 10);
    return t > 0 && Date.now() - t < PENDING_TTL_MS;
  } catch { return false; }
};

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { hour: "numeric", minute: "2-digit", month: "short", day: "numeric" }); }
  catch { return ""; }
};

export default function MagicVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token") || "";
  const [phase, setPhase] = useState(() => (token ? (sameDeviceRequested() ? "verifying" : "confirm") : "error"));
  const [err, setErr] = useState(token ? null : { message: "This sign-in link is missing its code. Open the link from your email again.", code: "unknown" });
  const [renew, setRenew] = useState({ busy: false, sent: "", error: "" });
  const started = useRef(false);

  const verify = async () => {
    if (started.current) return;
    started.current = true;
    setPhase("verifying");
    try {
      const res = await api.post("/auth/magic-link/verify", { token });
      // Prove the session actually stuck in THIS browser before leaving the page. If the cookie
      // was dropped (mail-app browser, private window, blocked cookies) we say so here instead
      // of bouncing to the login page and letting the person burn the link again.
      let me = null;
      try { me = (await api.get("/auth/me")).data; } catch { me = null; }
      if (!me) {
        setErr({ code: "cookie", message: "You're verified, but this browser didn't keep the sign-in." });
        setPhase("error");
        started.current = false;
        return;
      }
      try { localStorage.removeItem(MAGIC_PENDING_KEY); } catch { /* ignore */ }
      setUser(me);
      // Invite accepted — first stop is picking the password they'll use from now on.
      navigate(me.has_password ? "/dashboard" : "/auth/create-password", { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(d && typeof d === "object" ? d : { message: errMsg(e, "Couldn't complete sign-in. Please try again."), code: "" });
      setPhase("error");
      started.current = false;
    }
  };

  useEffect(() => { if (phase === "verifying") verify(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const requestFresh = async () => {
    setRenew({ busy: true, sent: "", error: "" });
    try {
      const r = await api.post("/auth/magic-link/renew", { token });
      try { localStorage.setItem(MAGIC_PENDING_KEY, String(Date.now())); } catch { /* ignore */ }
      setRenew({ busy: false, sent: r.data?.message || "A fresh link is on its way.", error: "" });
    } catch (e) {
      setRenew({ busy: false, sent: "", error: errMsg(e, "Couldn't send a fresh link. Please use the sign-in page.") });
    }
  };

  return (
    <div className="min-h-screen bg-sand-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center" data-testid="magic-verify-page">
        {phase === "confirm" && (
          <div className="bg-white rounded-2xl border border-sand-200 p-8" data-testid="magic-confirm">
            <div className="mx-auto h-12 w-12 rounded-2xl bg-redline-50 flex items-center justify-center mb-4">
              <Mail className="h-6 w-6 text-redline-500" strokeWidth={1.75} />
            </div>
            <h1 className="font-heading font-bold text-xl text-ink">Finish signing in</h1>
            <p className="text-sm text-muted-foreground mt-2">You're one tap from your console. This confirms a person — not your mail app or a security scanner — is opening the link.</p>
            <button data-testid="magic-continue" onClick={verify}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink text-white font-semibold px-6 py-3 hover:bg-ink/90 transition-colors">
              Continue to sign in <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </button>
            <p className="text-xs text-muted-foreground mt-4">Didn't ask for this link? Just close this tab.</p>
          </div>
        )}

        {phase === "verifying" && (
          <div data-testid="magic-verifying">
            <Loader2 className="h-10 w-10 animate-spin text-redline-500 mx-auto" />
            <p className="mt-4 text-muted-foreground">Signing you in…</p>
          </div>
        )}

        {phase === "error" && err && (
          <div className="bg-white rounded-2xl border border-sand-200 p-8" data-testid="magic-error">
            <div className="mx-auto h-12 w-12 rounded-2xl bg-danger-50 flex items-center justify-center mb-4">
              <ShieldAlert className="h-6 w-6 text-danger-600" strokeWidth={1.75} />
            </div>
            <h1 className="font-heading font-bold text-xl text-ink">{err.code === "cookie" ? "Almost there" : "Sign-in link problem"}</h1>
            <p className="text-sm text-muted-foreground mt-2" data-testid="magic-error-message">{err.message}</p>
            {err.code === "cookie" && (
              <div className="text-xs text-ink/70 mt-3 rounded-xl bg-sand-50 border border-sand-200 px-3 py-2 text-left space-y-1" data-testid="magic-cookie-help">
                <p>Usually one of these:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>You opened the link inside a mail app — tap its menu and choose <span className="font-semibold">Open in browser</span>, then tap Continue again within 10 minutes.</li>
                  <li>A private/incognito window or a "block all cookies" setting — open the link in a normal window.</li>
                </ul>
              </div>
            )}
            {err.code === "used" && err.first_opened_at && (
              <p className="text-xs text-ink/60 mt-3 rounded-xl bg-sand-50 border border-sand-200 px-3 py-2" data-testid="magic-first-opened">
                It was first opened {fmtTime(err.first_opened_at)} by {err.opened_by || "another device"}.
                {/(security|scanner)/i.test(err.opened_by || "") && " Your email system checks links before you see them — that used this one up."}
              </p>
            )}
            {renew.sent ? (
              <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 flex items-start gap-2 text-left" data-testid="magic-renew-sent">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> <span>{renew.sent}</span>
              </div>
            ) : (
              <>
                {token && (
                  <button data-testid="magic-renew" onClick={requestFresh} disabled={renew.busy}
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink text-white font-semibold px-6 py-3 hover:bg-ink/90 transition-colors disabled:opacity-60">
                    {renew.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" strokeWidth={1.75} />}
                    {renew.busy ? "Sending…" : "Email me a fresh link"}
                  </button>
                )}
                {renew.error && <p className="text-xs text-danger-700 mt-2" data-testid="magic-renew-error">{renew.error}</p>}
              </>
            )}
            <Link to="/login" data-testid="magic-back-to-login" className="inline-block mt-4 text-sm font-semibold text-redline-600 hover:text-redline-700">Back to sign in</Link>
          </div>
        )}
      </div>
    </div>
  );
}
